import React, { useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../utils/api';

interface ConsultingRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConsultingRequestModal: React.FC<ConsultingRequestModalProps> = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [vineyard, setVineyard] = useState('');
  const [goal, setGoal] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedGoal = goal.trim();
    const trimmedVineyard = vineyard.trim();
    const trimmedMessage = message.trim();
    if (!trimmedName || !trimmedEmail || !trimmedVineyard || !trimmedGoal) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/activity/public/consulting-request', {
        name: trimmedName,
        email: trimmedEmail,
        goal: trimmedGoal,
        vineyard_name: trimmedVineyard,
        ...(trimmedMessage ? { message: trimmedMessage } : {}),
      });
      setSent(true);
      setName('');
      setEmail('');
      setVineyard('');
      setGoal('');
      setMessage('');
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      const detail = res?.data?.detail;
      if (res?.status === 429) {
        setError('Please wait a minute before sending another request.');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Unable to submit right now. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="bg-vineyard-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-grape-200 bg-gradient-to-b from-grape-800 to-grape-600 rounded-t-lg">
          <h2 className="text-lg font-semibold text-white">Request your video consult</h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-grape-200 hover:text-white rounded-full p-1.5 transition-colors disabled:opacity-60"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          {sent ? (
            <p className="text-leaf-600 text-sm">
              Thanks! Your consulting request was sent. We&apos;ll reach out soon and you&apos;ll get a confirmation email.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="consult-name" className="block text-xs font-medium text-wood-700 mb-1">Name</label>
                  <input
                    id="consult-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                    maxLength={255}
                    required
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor="consult-email" className="block text-xs font-medium text-wood-700 mb-1">Email</label>
                  <input
                    id="consult-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                    maxLength={255}
                    required
                    disabled={submitting}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="consult-vineyard" className="block text-xs font-medium text-wood-700 mb-1">Vineyard name</label>
                <input
                  id="consult-vineyard"
                  type="text"
                  value={vineyard}
                  onChange={(e) => setVineyard(e.target.value)}
                  placeholder="Vineyard name"
                  className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                  maxLength={255}
                  required
                  disabled={submitting}
                />
              </div>
              <div>
                <label htmlFor="consult-goal" className="block text-xs font-medium text-wood-700 mb-1">Main goal</label>
                <input
                  id="consult-goal"
                  type="text"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="e.g. Design agentic workflows for my blocks"
                  className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                  maxLength={1000}
                  required
                  disabled={submitting}
                />
              </div>
              <div>
                <label htmlFor="consult-message" className="block text-xs font-medium text-wood-700 mb-1">Optional notes</label>
                <textarea
                  id="consult-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Any details you’d like to share"
                  rows={3}
                  className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent resize-y"
                  maxLength={5000}
                  disabled={submitting}
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <p className="text-xs text-wood-600 border-t border-wood-200 pt-3 mt-1">
                You will be sent an invoice for $100 at the time of the video call.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="px-3 py-2 text-sm font-medium text-wood-700 hover:bg-metal-100 rounded border border-wood-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !name.trim() || !email.trim() || !vineyard.trim() || !goal.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-grape-600 hover:bg-grape-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Request video consult'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultingRequestModal;
